/**
 * The main menu — the three-line button in the header.
 *
 * WHAT IT IS FOR. The TabBar carries the five destinations a trainer uses every
 * day, and everything else in the app is reachable only from inside a page that
 * happens to link to it. Bulk Update is three taps behind an action bar nobody
 * scrolls to; the Wish List is behind the Dex; Custom Dexes and Scan History
 * are behind Settings-adjacent corners. They are good features that a trainer
 * finds by accident or not at all. This is the one place that lists them.
 *
 * IT REPLACED THE PRO PILL, which spent a permanent slot in the header telling
 * the trainer something they already knew. The tier is still the first thing in
 * the menu — it is a status, and a status belongs where you go to check it, not
 * on screen forever.
 *
 * WHY A DROPDOWN AND NOT A SHEET. It is anchored to its own button, so the
 * button explains the panel. A bottom sheet would arrive from somewhere else,
 * and the app already uses sheets for "do this thing now" rather than for
 * navigation.
 *
 * THE BACK BUTTON SITS TO ITS LEFT on sub-pages. That pairing is deliberate and
 * it is why the trigger is an icon: the outgoing PRO pill was wider than this
 * button, so the left cluster is NARROWER than it was even with both controls
 * present, and the centred logo has more room rather than less.
 */
import { useRef, useState, useSyncExternalStore, type ComponentType, type CSSProperties } from "react";
import { useLocation } from "wouter";
import { MegaIcon } from "@/components/MegaIcon";
import {
  ArrowLeftRight,
  Calendar,
  ChevronRight,
  Crown,
  Gift,
  Grid3x3,
  Heart,
  History,
  Layers,
  LayoutGrid,
  Map,
  Menu,
  Scale,
  Search,
  Settings as SettingsIcon,
  Shield,
  Sparkles,
  Swords,
  Tag,
  Trash2,
  User,
  Users,
  Zap, PenLine, GraduationCap, Radar, Trophy } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ComingSoonDialog } from "@/components/ComingSoonDialog";
import { FriendPickerDialog } from "@/components/FriendPickerDialog";
import { useSession, hasProAccess, ULTIMATE_VISIBLE } from "@/components/SessionProvider";
import { useAuthOptional } from "@/components/AuthProvider";
import { isTourMenuOpen, subscribeTourMenuOpen } from "@/lib/tourMenuOpen";
import { confirmPageExit } from "@/lib/pageExitGuard";
import { toggleCopyEdit } from "@/lib/copyEditMode";
import { openWhyPro } from "@/components/WhyProSheet";

/**
 * What a row does when tapped.
 *
 * `route` is the plain case. The other two exist because a menu is a list of
 * DESTINATIONS and two kinds of thing in this app are not: Compare and Trade
 * are about a pair of trainers, so they have to ask which friend first; and My
 * Pokémon and Raid are not built yet, so they say so rather than 404ing or
 * quietly missing from a menu that claims to list everything.
 */
type MenuAction =
  | { kind: "route"; href: string }
  | { kind: "friend"; picker: "compare" | "trade" }
  | { kind: "soon"; soon: "raid" }
  /** Admin only: flips the in-app copy editor (lib/copyEditMode). */
  | { kind: "copyEdit" };

interface MenuLink {
  key: string;
  label: string;
  /** The one-liner under the label. Says what the page is FOR, not what it is. */
  hint?: string;
  action: MenuAction;
  /** A lucide icon, or any component taking className / style (MegaIcon). */
  Icon: ComponentType<{ className?: string; style?: CSSProperties; "aria-hidden"?: boolean | "true" | "false" }>;
  /** Indented under the entry above it, as one of its tools. */
  nested?: boolean;
}

/**
 * The menu, in the order a trainer thinks about the app.
 *
 * Nexus Dex first, because it is the thing they came for, with its two buried
 * tools directly under it — that pairing is the whole reason this menu exists.
 * Then the people, then the planning, then their own profile. Settings is last
 * and separated: it is where you go when something is wrong, not where you go
 * to do something.
 */
/**
 * What each unbuilt entry promises, in one line.
 *
 * The same line is the row's hint and the dialog's blurb, so the menu and the
 * dialog cannot describe the feature differently. Kept to a headline on purpose:
 * a bulleted spec for something unbuilt reads as a commitment to each bullet.
 */
export const COMING_SOON = {
  raid: { title: "Raid", blurb: "Join raids and more." },
} as const;

export const MENU_LINKS: MenuLink[] = [
  // ORDER (owner, 2026-09-19): the tab bar's order, left to right, with each
  // page's tools nested under it. The tier row and Settings sit outside this
  // list, first and last.
  // Tutorials sit high (owner ask, 2026-09-11): the last row of a menu this
  // long is below the fold on a phone, and a new trainer needs this one first.
  { key: "walkthroughs", label: "Tutorials", hint: "Walkthroughs of every feature, step by step", action: { kind: "route", href: "/walkthroughs" }, Icon: GraduationCap },
  { key: "search", label: "Search", hint: "Build a Pokémon GO search string", action: { kind: "route", href: "/trade" }, Icon: Search },
  { key: "profile", label: "Profile", hint: "Your totals and what to chase next", action: { kind: "route", href: "/" }, Icon: User },
  { key: "friends", label: "Friends", hint: "Everyone you're connected with", action: { kind: "route", href: "/friends" }, Icon: Users },
  { key: "compare", label: "Compare", hint: "Head to head with one friend", action: { kind: "friend", picker: "compare" }, Icon: Scale, nested: true },
  { key: "trade", label: "Trade", hint: "What the two of you could swap", action: { kind: "friend", picker: "trade" }, Icon: ArrowLeftRight, nested: true },
  { key: "dex", label: "Nexus Dex", hint: "Every dex you're filling", action: { kind: "route", href: "/dex" }, Icon: LayoutGrid },
  // 2026-09-03: the trainer's Megas, their levels, and the best one to evolve.
  { key: "mega", label: "Mega Evolutions", hint: "Your Megas, their levels, and the best one to evolve", action: { kind: "route", href: "/mega" }, Icon: MegaIcon, nested: true },
  { key: "bulk", label: "Bulk Update", hint: "Tick off a whole region at once", action: { kind: "route", href: "/dex?bulk=1" }, Icon: Grid3x3, nested: true },
  { key: "wish", label: "Wish List", hint: "What you're chasing, shared with friends", action: { kind: "route", href: "/wish-list" }, Icon: Heart, nested: true },
  // SHIPPED 2026-08-20: the trainer's individual caught Pokémon (IVs, moves and
  // more), fed by the live scanner reading the in-game caught-Pokémon page.
  { key: "myPokemon", label: "My Pokémon", hint: "Your Pokémon: IVs, moves and more", action: { kind: "route", href: "/my-pokemon" }, Icon: Heart },
  // 2026-08-31: both pages shipped without a menu row, so the only way in was
  // a link from another page. They belong under My Pokémon: both act on it.
  { key: "cleanup", label: "Box Cleanup", hint: "Free up storage without losing a keeper", action: { kind: "route", href: "/cleanup" }, Icon: Trash2, nested: true },
  { key: "naming", label: "Naming", hint: "Put the IVs in the name, ready to paste into GO", action: { kind: "route", href: "/naming" }, Icon: Tag, nested: true },
  { key: "stardust", label: "Stardust Planner", hint: "The best power-ups for the dust you have", action: { kind: "route", href: "/stardust-planner" }, Icon: Sparkles, nested: true },
  { key: "battle", label: "Battle Hub", hint: "The GBL meta, and what it runs", action: { kind: "route", href: "/battle" }, Icon: Swords },
  // 2026-09-15: Team GO Rocket line-ups, your best three against each, and
  // the dex rows this rotation's shadows could fill.
  { key: "rocket", label: "Rocket", hint: "Line-ups, your best three, and what they fill in your Nexus Dex", action: { kind: "route", href: "/rocket" }, Icon: Radar, nested: true },
  // 2026-09-28: Max Battles: this rotation's Dynamax / Gigantamax bosses,
  // your best team from your own Max copies, and your Max coverage.
  { key: "dynamax", label: "MAX Battles", hint: "This rotation's Max bosses, your best team, and your Max coverage", action: { kind: "route", href: "/dynamax" }, Icon: Zap, nested: true },
  // 2026-09-19: PokéStop Showcases, type by type, your best entry scored.
  { key: "showcases", label: "Showcases", hint: "Your biggest, type by type, scored for PokéStop Showcases", action: { kind: "route", href: "/showcases" }, Icon: Trophy, nested: true },
  { key: "scans", label: "Scan History", hint: "Every scan, and what it changed", action: { kind: "route", href: "/scan-history" }, Icon: History },
  // DORMANT AGAIN (2026-08-17). The backend is live and the screens work, but
  // the feature is not ready to launch, so the menu says so rather than opening
  // something unfinished. /raids still resolves by direct URL for review on a
  // real device. Waking it back up is this one line:
  //   action: { kind: "route", href: "/raids" }
  { key: "raid", label: "Raid", hint: COMING_SOON.raid.blurb, action: { kind: "soon", soon: "raid" }, Icon: Shield },
  { key: "calendar", label: "Calendar", hint: "What's on, and what you're missing from it", action: { kind: "route", href: "/calendar" }, Icon: Calendar },
  { key: "bonuses", label: "Active Bonuses", hint: "What's boosted right now, and what unlocks next", action: { kind: "route", href: "/bonuses" }, Icon: Gift, nested: true },
];

/**
 * TEMPORARY (2026-08-20): the pages nothing links to, surfaced for on-device
 * review so their fate can be decided — keep, relink, or delete. Rendered only
 * for admin accounts, so no ordinary trainer ever sees them. Delete this list,
 * its render block below, and the review routes in App.tsx together.
 */
export const REVIEW_LINKS: MenuLink[] = [
  { key: "reviewPowerScouter", label: "Power Scouter", hint: "The parked Ultimate battle overlay", action: { kind: "route", href: "/power-scouter" }, Icon: Zap },
  { key: "reviewRaids", label: "Raid Hub", hint: "Dormant behind the Coming Soon entry", action: { kind: "route", href: "/raids" }, Icon: Shield },
  { key: "reviewSubDex", label: "Forms Sub-Dex", hint: "Unown, as a sample forms page", action: { kind: "route", href: "/dex/pokemon/201/forms" }, Icon: Layers },
  { key: "reviewFeaturesMap", label: "Features Map", hint: "The everything-inside overview card", action: { kind: "route", href: "/review/features-map" }, Icon: Map },
  // Not temporary: the owner's copy editor (components/CopyEditMode). Tap any
  // text on any page to change it or remove its block, saved to the Worker and
  // live on every device without a build.
  { key: "copyEdit", label: "Edit copy", hint: "Tap any text to change or hide it, no build needed", action: { kind: "copyEdit" }, Icon: PenLine },
];


/** What the tier row says, and where tapping it goes. */
export function tierRow(tier: string): { label: string; sub: string; manage: boolean } {
  if (tier === "pro") return { label: "Pro", sub: "Manage subscription", manage: true };
  if (tier === "ultimate" && ULTIMATE_VISIBLE) {
    return { label: "Ultimate", sub: "Manage subscription", manage: true };
  }
  if (tier === "ultimate_trial" && ULTIMATE_VISIBLE) {
    return { label: "Ultimate Trial", sub: "Manage subscription", manage: true };
  }
  if (tier === "ultimate" || tier === "ultimate_trial") {
    return { label: "Pro", sub: "Manage subscription", manage: true };
  }
  if (tier === "trial") return { label: "Trial", sub: "See what Pro unlocks", manage: false };
  return { label: "Basic", sub: "See what Pro unlocks", manage: false };
}

/**
 * How far the panel sits from the screen edge when it opens, in px.
 *
 * The header's own left padding, so the menu lines up with the back arrow and
 * the page gutter rather than floating in from nowhere.
 */
const SCREEN_GUTTER = 16;

export function MainMenu() {
  const [, setLocation] = useLocation();
  const { tier, openPaywall } = useSession();
  const [selfOpen, setSelfOpen] = useState(false);
  /**
   * The guided tour's menu step holds the panel open so it can spotlight the
   * LIST rather than the closed button that hides it. While it does, the tour
   * owns the panel's lifetime completely: `setOpen` below drops every open and
   * close request, so an outside tap, Escape, or the tour's own scrim cannot
   * dismiss it mid-step. It closes when the flag clears. See lib/tourMenuOpen.
   */
  const tourOpen = useSyncExternalStore(subscribeTourMenuOpen, isTourMenuOpen, () => false);
  const open = selfOpen || tourOpen;
  const setOpen = (value: boolean) => {
    if (tourOpen) return;
    setSelfOpen(value);
  };
  /**
   * `inert` while the tour holds the panel, which takes the whole subtree out of
   * focus and hit-testing.
   *
   * The tour's scrim already sits above the panel, so nothing in here can be
   * TAPPED. The keyboard was the hole: Radix moves focus into the content on
   * open, and from there an ArrowDown lands on a menu row and an Enter follows
   * the link, navigating out of a tour that has no idea it left. `inert` closes
   * that without a key-swallowing handler that would also eat the tour's own
   * Enter-is-Next binding.
   *
   * The empty-string value and the cast are React 18: it has no typing for
   * `inert` and only forwards unknown attributes when their value is a string.
   */
  const inertWhileTour = tourOpen ? ({ inert: "" } as { inert?: string }) : {};
  /**
   * The panel opens against the SCREEN, not against its button.
   *
   * Radix aligns a dropdown to its trigger, and this trigger moves: on a
   * sub-page the back arrow pushes it right, so the menu opened inset by the
   * width of an arrow and read as though it had come from somewhere else. The
   * offset is MEASURED from the trigger's own position rather than hardcoded to
   * the arrow's width, so it stays right if the left cluster ever changes again
   * (reported on device, 2026-08-14).
   */
  const triggerRef = useRef<HTMLButtonElement>(null);
  const alignOffset = (() => {
    const left = triggerRef.current?.getBoundingClientRect().left;
    return left == null ? 0 : -Math.max(0, left - SCREEN_GUTTER);
  })();
  /** Which friend picker is up, if any: the menu chose the destination, the
   *  dialog chooses the trainer. */
  const [picker, setPicker] = useState<null | "compare" | "trade">(null);
  const [soon, setSoon] = useState<null | keyof typeof COMING_SOON>(null);
  const row = tierRow(tier);
  const isPro = hasProAccess(tier);
  /** Gates the temporary Review section. Optional: the header renders on auth
   *  pages too, where there is no provider value yet. */
  const isAdmin = useAuthOptional()?.account?.isAdmin === true;

  // Navigation defers to the current page's exit guard (species-page Manual
  // Edit with an unresolved regional answer — lib/pageExitGuard). The menu
  // still closes first so a blocked guard's own sheet is not buried under it.
  const go = (href: string) => {
    setOpen(false);
    if (!confirmPageExit()) return;
    setLocation(href);
  };

  const choose = (link: MenuLink) => {
    // The menu closes first in every branch: a dropdown still open behind a
    // dialog is two overlays deep and traps focus between them.
    setOpen(false);
    if (link.action.kind === "route") {
      if (!confirmPageExit()) return;
      setLocation(link.action.href);
    } else if (link.action.kind === "friend") setPicker(link.action.picker);
    else if (link.action.kind === "copyEdit") toggleCopyEdit();
    else setSoon(link.action.soon);
  };

  return (
    /* modal={false} while the tour drives it. Radix's modal mode puts
       `pointer-events: none` on the body and hands interaction to the panel
       alone, which would leave the tour's own card (Next, Back, Skip) dead to
       the touch behind its overlay. */
    <DropdownMenu open={open} onOpenChange={setOpen} modal={!tourOpen}>
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          data-testid="button-main-menu"
          aria-label="Menu"
          className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-elev-1)] transition-colors"
        >
          <Menu className="w-[18px] h-[18px]" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        alignOffset={alignOffset}
        sideOffset={8}
        data-testid="main-menu"
        /* Shorter under the tour: the step's copy card is placed BELOW the
           spotlight ring, and a panel at its usual 70dvh leaves no room for it
           on a phone, so the card would be clamped over the very list it is
           describing. 44dvh keeps most of the menu visible with the card clear
           underneath it, and the rest is a scroll away as always. */
        className={`w-64 overflow-y-auto p-1.5 ${tourOpen ? "max-h-[44dvh]" : "max-h-[70dvh]"}`}
        /* Under the tour the panel is scenery, not a control: the step, not the
           trainer's next tap, decides when it closes. */
        onInteractOutside={tourOpen ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={tourOpen ? (e) => e.preventDefault() : undefined}
        {...inertWhileTour}
      >
        {/* The tier, first. A status, and the one thing in here that is about
            the account rather than about a page. */}
        <button
          type="button"
          data-testid="main-menu-tier"
          onClick={() => {
            setOpen(false);
            if (row.manage) {
              if (!confirmPageExit()) return;
              setLocation("/settings/billing");
            } else openWhyPro();
          }}
          className="w-full text-left rounded-lg px-2.5 py-2 flex items-center gap-2.5 hover-elevate"
          style={{
            background: isPro ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
            border: `1px solid ${isPro ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
          }}
        >
          <Crown
            className="w-4 h-4 shrink-0"
            style={{ color: isPro ? "var(--accent-fill)" : "var(--text-tertiary)" }}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span
              className="block text-[12px] font-black uppercase tracking-wider"
              style={{ color: isPro ? "var(--accent-fill)" : "var(--text-primary)" }}
            >
              {row.label}
            </span>
            <span className="block text-[10px]" style={{ color: "var(--text-tertiary)" }}>
              {row.sub}
            </span>
          </span>
          <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />
        </button>

        <DropdownMenuSeparator className="my-1.5" />

        {MENU_LINKS.map((link) => (
          <DropdownMenuItem
            key={link.key}
            data-testid={`main-menu-${link.key}`}
            onSelect={() => choose(link)}
            className={`gap-2.5 py-2 cursor-pointer ${link.nested ? "pl-6" : ""}`}
          >
            <link.Icon
              className="w-4 h-4 shrink-0"
              style={{ color: link.nested ? "var(--text-tertiary)" : "var(--accent-fill)" }}
              aria-hidden
            />
            <span className="min-w-0 flex-1">
              <span
                className={`flex items-center gap-1.5 truncate ${link.nested ? "text-[12px] font-semibold" : "text-[13px] font-bold"}`}
                style={{ color: "var(--text-primary)" }}
              >
                {link.label}
                {/* Said BEFORE the tap, so the dialog confirms rather than
                    surprises. */}
                {link.action.kind === "soon" && (
                  <span
                    className="text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded-full shrink-0"
                    style={{ background: "var(--surface-sunken)", color: "var(--text-tertiary)" }}
                  >
                    Soon
                  </span>
                )}
              </span>
              {link.hint && (
                <span className="block text-[10px] leading-tight" style={{ color: "var(--text-tertiary)" }}>
                  {link.hint}
                </span>
              )}
            </span>
          </DropdownMenuItem>
        ))}

        {/* TEMPORARY admin-only Review section — unlinked pages surfaced for an
            on-device look before deciding their fate. See REVIEW_LINKS. */}
        {isAdmin && (
          <>
            <DropdownMenuSeparator className="my-1.5" />
            <p
              className="px-2.5 pb-1 pt-0.5 text-[9px] font-black uppercase tracking-wider"
              style={{ color: "var(--text-tertiary)" }}
            >
              Review · admin only
            </p>
            {REVIEW_LINKS.map((link) => (
              <DropdownMenuItem
                key={link.key}
                data-testid={`main-menu-${link.key}`}
                onSelect={() => choose(link)}
                className="gap-2.5 py-2 cursor-pointer"
              >
                <link.Icon className="w-4 h-4 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12px] font-semibold" style={{ color: "var(--text-primary)" }}>
                    {link.label}
                  </span>
                  {link.hint && (
                    <span className="block text-[10px] leading-tight" style={{ color: "var(--text-tertiary)" }}>
                      {link.hint}
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}

        <DropdownMenuSeparator className="my-1.5" />

        {/* Settings last, and below the line: it is where you go when something
            is wrong, not where you go to do something. */}
        <DropdownMenuItem
          data-testid="main-menu-settings"
          onSelect={() => go("/settings")}
          className="gap-2.5 py-2 cursor-pointer"
        >
          <SettingsIcon className="w-4 h-4 shrink-0" style={{ color: "var(--text-secondary)" }} aria-hidden />
          <span className="text-[13px] font-bold" style={{ color: "var(--text-primary)" }}>
            Settings
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/* OUTSIDE the dropdown content, because Radix unmounts that subtree when
          the menu closes and the menu closes as a dialog opens — but mounted
          only WHILE OPEN. The picker reads the friends list, and mounting it
          with every header on every page would fire that query for a dialog
          nobody has asked for. Nothing is fetched until the question is. */}
      {picker != null && (
      <FriendPickerDialog
        open
        onOpenChange={(v) => setPicker(v ? picker : null)}
        title={picker === "trade" ? "Trade with which friend?" : "Compare with which friend?"}
        description={
          picker === "trade"
            ? "Their trade list, your leads, and what the two of you could swap."
            : "Head to head across every dex, and how you're both tracking."
        }
        onPick={(accountId) => {
          if (!confirmPageExit()) return;
          setLocation(`/friends/${accountId}/${picker === "trade" ? "trades" : "compare"}`);
        }}
      />
      )}

      {soon != null && (
        <ComingSoonDialog
          open
          onOpenChange={(v) => setSoon(v ? soon : null)}
          title={COMING_SOON[soon].title}
          blurb={COMING_SOON[soon].blurb}
        />
      )}
    </DropdownMenu>
  );
}
